import { useCallback, useState } from "react";

import { useEmployeeProfilesTrackingService } from "app/pages/sales-intelligence/modules/employee-profiles/hooks/useEmployeeProfilesTrackingService";
import { useEmployeeProfileToasts } from "app/pages/sales-intelligence/modules/employee-profiles/hooks/useEmployeeProfileToasts";
import { getDeleteProfileErrorMessage } from "app/pages/sales-intelligence/modules/employee-profiles/utils/getDeleteProfileErrorMessage";
import { summarizeBulkDelete } from "app/pages/sales-intelligence/modules/employee-profiles/utils/summarizeBulkDelete";
import { isSharedEmployeeProfile } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/helpers/accessLevelFilters";
import { useDeleteContactProfileMutation } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/queries/useDeleteContactProfileMutation";
import type { ProfileDto } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";
import { sentryErrorTrackerWithScope } from "app/pages/sales-intelligence/utils/tracking/sentry";

type BulkDeleteRequest = {
    ids: string[];
    isShared: boolean;
};

export const useEmployeeProfilesDeletion = () => {
    const { showSuccess, showError, showErrorMessage } = useEmployeeProfileToasts();
    const employeeProfilesTracking = useEmployeeProfilesTrackingService();

    const [profileToDelete, setProfileToDelete] = useState<ProfileDto | undefined>(undefined);
    const [bulkDeleteRequest, setBulkDeleteRequest] = useState<BulkDeleteRequest | undefined>(undefined);
    const [clearSelectionTokens, setClearSelectionTokens] = useState({ shared: 0, private: 0 });

    const { mutateAsync: deleteProfile, isPending: isDeleting } = useDeleteContactProfileMutation();

    const handleRequestDelete = useCallback(
        (profile: ProfileDto) => {
            employeeProfilesTracking.trackDeleteOpen();
            setProfileToDelete(profile);
        },
        [employeeProfilesTracking],
    );

    const handleCancelDelete = () => {
        employeeProfilesTracking.trackDeleteCancel();
        setProfileToDelete(undefined);
    };

    const handleConfirmDelete = async () => {
        if (!profileToDelete) {
            return;
        }
        employeeProfilesTracking.trackDeleteConfirm();
        try {
            await deleteProfile(profileToDelete.id);
            setProfileToDelete(undefined);
            showSuccess("si.employee-profiles.delete.toast.success");
        } catch (error) {
            const serverMessage = await getDeleteProfileErrorMessage(error);

            // A rejected delete is an expected outcome rather than a fault, so it is not reported to Sentry.
            if (serverMessage) {
                showErrorMessage(serverMessage);
                return;
            }

            sentryErrorTrackerWithScope({
                errorMessage: "Failed to delete employee profile",
                e: error,
                tags: { component: "employee-profiles" },
                params: { extra: { profileId: profileToDelete.id } },
            });
            showError();
        }
    };

    const handleRequestBulkDelete = (ids: string[], isShared: boolean) => {
        setBulkDeleteRequest({ ids, isShared });
    };

    const handleConfirmBulkDelete = async () => {
        if (!bulkDeleteRequest) {
            return;
        }
        const { ids, isShared } = bulkDeleteRequest;
        employeeProfilesTracking.trackBulkDelete(ids.length);

        // Every request is dispatched regardless of how the others settle, so a single rejection must not
        // hide the deletes that did go through.
        const results = await Promise.allSettled(ids.map((id) => deleteProfile(id)));
        const { outcome, deletedCount, failedIds, firstFailure } = summarizeBulkDelete(ids, results);

        setBulkDeleteRequest(undefined);
        setClearSelectionTokens((tokens) =>
            isShared ? { ...tokens, shared: tokens.shared + 1 } : { ...tokens, private: tokens.private + 1 },
        );

        if (outcome === "success") {
            showSuccess("si.employee-profiles.delete.toast.success");
            return;
        }

        const serverMessage = outcome === "failure" ? await getDeleteProfileErrorMessage(firstFailure) : undefined;

        if (serverMessage) {
            showErrorMessage(serverMessage);
            return;
        }

        sentryErrorTrackerWithScope({
            errorMessage: "Failed to delete selected employee profiles",
            e: firstFailure,
            tags: { component: "employee-profiles" },
            params: { extra: { profileIds: ids, failedProfileIds: failedIds } },
        });

        if (outcome === "failure") {
            showError();
            return;
        }

        showError("si.employee-profiles.delete.toast.partial_failure", {
            deleted: deletedCount,
            total: ids.length,
        });
    };

    return {
        isDeleting,
        clearSelectionTokens,
        isDeleteModalOpen: profileToDelete !== undefined,
        isProfileToDeleteShared: profileToDelete ? isSharedEmployeeProfile(profileToDelete) : false,
        profileToDeleteName: profileToDelete?.name ?? "",
        isBulkDeleteModalOpen: bulkDeleteRequest !== undefined,
        isBulkDeleteShared: bulkDeleteRequest?.isShared ?? false,
        bulkDeleteCount: bulkDeleteRequest?.ids.length ?? 0,
        handleRequestDelete,
        handleCancelDelete,
        handleConfirmDelete,
        handleRequestBulkDelete,
        handleCancelBulkDelete: () => setBulkDeleteRequest(undefined),
        handleConfirmBulkDelete,
    };
};
